import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { isBoolean, snakeCase } from 'lodash'

import { Label } from 'components/label/Label'

import styles from './Input.module.scss'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'

export const Input = ({
  type = 'text',
  isAutoFocus = false,
  isValid = true,
  showErrorText = true,
  value,
  onChange,
  placeholder = '',
  maxLength,
  min,
  max,
  label,
  testId,
  disabled = false,
  readOnly = false,
  error = '',
  className = '',
  inputClassName = '',
  size = 'regular',
  icon,
  hasClearIcon = false,
  onClear,
  validationPattern = null,
  setInputRef,
  isRequired,
  mask = null,
  checked,
  onFocus,
  onBlur,
  shouldBeClearable = false,
}) => {
  const containerClassName = classNames(
    styles.input,
    {
      [styles.withIcon]: !!icon,
      [styles.withClearIcon]: hasClearIcon,
      [styles.disabled]: disabled,
      [styles.readOnly]: readOnly,
      [styles.error]: !!error || !isValid,
      [styles.small]: size === 'small',
    },
    className,
  )
  const inputId = label ? `${snakeCase(label)}-input` : undefined
  const radioProps = {}

  if (isBoolean(checked)) {
    radioProps.checked = checked
  }

  return (
    <div className={containerClassName} data-test-id={testId}>
      {label && (
        <label className={styles.wrapper} htmlFor={inputId}>
          <Label isRequired={isRequired}>{label}</Label>
        </label>
      )}

      <div className={styles.wrapper}>
        {mask && <div className={styles.mask}>{mask}</div>}

        {!mask && (
          <input
            autoFocus={isAutoFocus}
            id={inputId}
            className={inputClassName}
            type={type}
            value={value}
            placeholder={placeholder}
            maxLength={maxLength}
            min={min}
            max={max}
            onChange={e =>
              validationPattern
                ? validationPattern(e.target.value) && onChange(e.target.value)
                : onChange(e.target.value)
            }
            disabled={disabled}
            ref={setInputRef}
            readOnly={readOnly}
            onFocus={onFocus}
            onBlur={onBlur}
            {...radioProps}
          />
        )}

        {icon && icon}

        {hasClearIcon && (value || shouldBeClearable) && (
          <span className={classNames(styles.iconWrapper, styles.clearIcon)} onClick={onClear}>
            <CrossIcon height="10px" width="10px" />
          </span>
        )}
      </div>

      {error && showErrorText && <div className={styles.errorBlock}>{error}</div>}
    </div>
  )
}

Input.propTypes = {
  type: PropTypes.oneOf(['text', 'number', 'password', 'radio']),
  isAutoFocus: PropTypes.bool,
  isValid: PropTypes.bool,
  showErrorText: PropTypes.bool,
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  ref: PropTypes.any,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  testId: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  size: PropTypes.oneOf(['regular', 'small']),
  icon: PropTypes.string,
  hasClearIcon: PropTypes.bool,
  isRequired: PropTypes.bool,
  readOnly: PropTypes.bool,
  onClear: PropTypes.func,
  validationPattern: PropTypes.func,
  setInputRef: PropTypes.any,
  mask: PropTypes.node,
  checked: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  shouldBeClearable: PropTypes.bool,
}
